import { Form, Formik } from "formik";
import { useState } from "react";
import { ApiRequestError } from "../../../api/util";
import { DelegationInviteFormValues } from "../../../interfaces/delegation";
import { useCreateDelegationInvitation } from "../../../hooks/delegation/useCreateDelegationInvitation";
import { delegationInviteValidationSchema } from "../../../util/validationSchema/delegationInvite";
import { Button } from "../../Button";
import { FieldWithLabel } from "../../form/FieldWithLabel";
import Spinner from "../../graphics/Spinner";
import Modal, { ModalRoot, ModalTrigger } from "../../Modal/Modal";
import DelegationPermissionsField from "./DelegationPermissionsField";

const getSubmitErrorMessage = (error: unknown) => {
  if (error instanceof ApiRequestError) return error.message;
  if (error instanceof Error) return error.message;
  return "Der opstod en fejl. Prøv igen senere.";
};

const initialValues: DelegationInviteFormValues = {
  invitedName: "",
  invitedLabel: "",
  deliveryEmail: "",
  permissions: 0,
  expiryDays: 14,
  hasDelegationExpiry: false,
  delegationValidDays: 365,
};

const DelegationInviteModal = () => {
  const createMutation = useCreateDelegationInvitation();
  const [open, setOpen] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  const isSubmitting = createMutation.isPending;

  const handleOpenChange = (nextOpen: boolean) => {
    if (isSubmitting) return;
    setOpen(nextOpen);
    if (!nextOpen) {
      setSubmitError(null);
      setIsSuccess(false);
    }
  };

  return (
    <ModalRoot
      controller={{
        open,
        setOpen,
        onOpenChange: handleOpenChange,
      }}
    >
      <ModalTrigger asChild>
        <Button
          id="delegation-invite-open"
          type="button"
          className="btn btn-primary"
          aria-label="Opret invitation"
        >
          Opret invitation
        </Button>
      </ModalTrigger>
      <Modal
        title="Opret invitation"
        description="Invitér en person til fuldmagt login"
        small
        dialog={isSubmitting}
        footer={
          isSuccess ? (
            <div className="d-flex justify-content-end px-4 pb-4">
              <Button
                id="delegation-invite-close"
                type="button"
                color="primary"
                onClick={() => handleOpenChange(false)}
              >
                Luk
              </Button>
            </div>
          ) : (
            <div className="d-flex justify-content-end gap-2 pb-4 px-4">
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => handleOpenChange(false)}
                disabled={isSubmitting}
              >
                Annuller
              </button>
              <Button
                id="delegation-invite-submit"
                type="submit"
                form="delegation-invite-form"
                color="primary"
                disabled={isSubmitting}
                aria-label="Opret invitation"
              >
                {isSubmitting ? (
                  <span className="d-inline-flex align-items-center gap-2">
                    <Spinner inline size={16} label="" />
                    Opretter...
                  </span>
                ) : (
                  "Opret invitation"
                )}
              </Button>
            </div>
          )
        }
      >
        {isSuccess ? (
          <div className="alert alert-success mb-0" role="alert">
            Invitationen er oprettet. Den inviterede modtager et link på e-mail.
          </div>
        ) : (
          <>
            {submitError ? (
              <div
                className="alert alert-danger d-flex align-items-start justify-content-between gap-3 mb-4"
                role="alert"
              >
                <span>{submitError}</span>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Luk fejlmeddelelse"
                  onClick={() => setSubmitError(null)}
                />
              </div>
            ) : null}

            <Formik
              initialValues={initialValues}
              validationSchema={delegationInviteValidationSchema}
              onSubmit={async (values, { resetForm }) => {
                setSubmitError(null);
                try {
                  await createMutation.mutateAsync({
                    permissions: values.permissions,
                    invitedName: values.invitedName.trim(),
                    invitedLabel: values.invitedLabel.trim() || null,
                    deliveryEmail: values.deliveryEmail.trim(),
                    expiryDays: values.expiryDays,
                    delegationValidDays: values.hasDelegationExpiry
                      ? values.delegationValidDays
                      : null,
                  });
                  setIsSuccess(true);
                  resetForm();
                } catch (error) {
                  setSubmitError(getSubmitErrorMessage(error));
                }
              }}
            >
              {({ values, setFieldValue, errors, touched, submitCount }) => (
                <Form id="delegation-invite-form">
                  <div className="vstack gap-4">
                    <FieldWithLabel
                      fieldId="delegation-invited-name"
                      fieldName="invitedName"
                      label="Modtager"
                      placeholder="Navn"
                      isRequired
                      touched={touched.invitedName}
                      error={errors.invitedName}
                    />
                    <FieldWithLabel
                      fieldId="delegation-invited-label"
                      fieldName="invitedLabel"
                      label="Betegnelse (valgfri)"
                      placeholder="Fx sekretær, kollega"
                      touched={touched.invitedLabel}
                      error={errors.invitedLabel}
                    />
                    <FieldWithLabel
                      fieldId="delegation-delivery-email"
                      fieldName="deliveryEmail"
                      fieldType="email"
                      label="E-mail til invitation"
                      placeholder="navn@firma.dk"
                      isRequired
                      touched={touched.deliveryEmail}
                      error={errors.deliveryEmail}
                    />
                    <FieldWithLabel
                      fieldId="delegation-expiry-days"
                      fieldName="expiryDays"
                      fieldType="number"
                      label="Invitation udløber (dage)"
                      helperText="Angiv hvor mange dage invitationen skal være gyldig. Når perioden udløber, kan linket ikke længere bruges til at acceptere fuldmagten."
                      isRequired
                      touched={touched.expiryDays}
                      error={errors.expiryDays}
                    />
                    <div className="form-check">
                      <input
                        id="delegation-has-expiry"
                        name="hasDelegationExpiry"
                        type="checkbox"
                        className="form-check-input"
                        checked={values.hasDelegationExpiry}
                        onChange={(event) =>
                          setFieldValue(
                            "hasDelegationExpiry",
                            event.target.checked,
                            true,
                          )
                        }
                      />
                      <label
                        className="form-check-label"
                        htmlFor="delegation-has-expiry"
                      >
                        Fuldmagten udløber
                      </label>
                      <div className="form-text">
                        Hvis valgt, angives hvor længe fuldmagten skal være
                        gyldig efter accept.
                      </div>
                    </div>
                    {values.hasDelegationExpiry ? (
                      <FieldWithLabel
                        fieldId="delegation-valid-days"
                        fieldName="delegationValidDays"
                        fieldType="number"
                        label="Fuldmagt varighed (dage)"
                        helperText="Angiv hvor mange dage fuldmagten skal være gyldig efter accept. (Maks. 3 år)"
                        isRequired
                        touched={touched.delegationValidDays}
                        error={errors.delegationValidDays}
                      />
                    ) : null}
                    <div>
                      <DelegationPermissionsField
                        permissions={values.permissions}
                        onChange={(perms) =>
                          setFieldValue("permissions", perms, true)
                        }
                        idPrefix="delegation-invite-perm"
                        isRequired
                      />
                      {(touched.permissions || submitCount > 0) &&
                      errors.permissions ? (
                        <div className="text-danger small mt-1">
                          {errors.permissions}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          </>
        )}
      </Modal>
    </ModalRoot>
  );
};

export default DelegationInviteModal;
